import React, { useCallback, useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardHeader,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CreateIcon from '@mui/icons-material/Create';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';
import BeatLoader from 'react-spinners/BeatLoader';
import { toast } from 'react-toastify';

const apiBase = 'https://serverdemo.ccsoftwares.com/vehicle-license';

const emptyRegisterForm = {
  vehicle_id: '',
  vehicle_number: '',
  model: '',
  reference: '',
  licensed_date: '',
  insured_date: '',
  insurance_agent_details: '',
};

const emptyCurrentForm = {
  vehicle_id: '',
  vehicle_number: '',
  model: '',
  reference: '',
  licensed_date: '',
  insured_date: '',
  insurance_agent_details: '',
  license_reference_or_note: '',
  insurance_reference_or_note: '',
};

const emptyLicenseForm = {
  vehicle_id: '',
  licensed_date: '',
  reference_or_note: '',
};

const emptyInsuranceForm = {
  vehicle_id: '',
  insured_date: '',
  insurance_agent_details: '',
  reference_or_note: '',
};

const emptyAlertForm = {
  id: '',
  email_address: '',
  name_or_description: '',
  status: 1,
};

const getErrorMessage = (error, fallback) => error?.response?.data?.error || fallback;

const formatDate = (value) => {
  if (!value) return '-';
  return String(value).slice(0, 10);
};

const toDateInputValue = (value) => (value ? String(value).slice(0, 10) : '');

const getVehicleLabel = (vehicle) => {
  if (!vehicle) return '';
  if (typeof vehicle === 'string') return vehicle;
  const number = vehicle.vehicle_number || '';
  const model = vehicle.model || vehicle.vehicle_model || '';
  return model ? `${number} - ${model}` : number;
};

const dateInputProps = {
  InputLabelProps: { shrink: true },
};

function RenewalStatusChip({ status }) {
  if (status === null || status === undefined) {
    return <Chip label="Not set" size="small" color="warning" variant="outlined" />;
  }

  return Number(status) === 1 ? (
    <Chip label="Current" size="small" color="primary" />
  ) : (
    <Chip label="Old" size="small" color="default" />
  );
}

function ActiveStatusChip({ status }) {
  return Number(status) === 1 ? (
    <Chip label="Active" size="small" color="primary" />
  ) : (
    <Chip label="Inactive" size="small" color="error" />
  );
}

function EmptyRow({ colSpan, loading, text = 'No records found' }) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} align="center">
        {loading ? 'Loading...' : text}
      </TableCell>
    </TableRow>
  );
}

function VehicleLicenseDetails() {
  const [activeTab, setActiveTab] = useState(0);
  const [vehicles, setVehicles] = useState([]);
  const [currentDetails, setCurrentDetails] = useState([]);
  const [licenseHistory, setLicenseHistory] = useState([]);
  const [insuranceHistory, setInsuranceHistory] = useState([]);
  const [upcomingRenewals, setUpcomingRenewals] = useState([]);
  const [alertEmails, setAlertEmails] = useState([]);

  const [currentLoading, setCurrentLoading] = useState(false);
  const [licenseLoading, setLicenseLoading] = useState(false);
  const [insuranceLoading, setInsuranceLoading] = useState(false);
  const [upcomingLoading, setUpcomingLoading] = useState(false);
  const [alertLoading, setAlertLoading] = useState(false);
  const [registerSaving, setRegisterSaving] = useState(false);
  const [currentSaving, setCurrentSaving] = useState(false);
  const [currentDeletingId, setCurrentDeletingId] = useState(null);
  const [insuranceSaving, setInsuranceSaving] = useState(false);

  const [currentFilters, setCurrentFilters] = useState({ vehicle_number: '', from_date: '', to_date: '' });
  const [licenseFilters, setLicenseFilters] = useState({
    vehicle_number: '',
    from_date: '',
    to_date: '',
    date_type: 'licensed_date',
  });
  const [insuranceFilters, setInsuranceFilters] = useState({
    vehicle_number: '',
    from_date: '',
    to_date: '',
    date_type: 'insured_date',
  });
  const [upcomingFilters, setUpcomingFilters] = useState({
    vehicle_number: '',
    from_date: '',
    to_date: '',
    renewal_type: 'All',
  });
  const [alertFilters, setAlertFilters] = useState({ search: '', status: '' });

  const [registerOpen, setRegisterOpen] = useState(false);
  const [currentEditOpen, setCurrentEditOpen] = useState(false);
  const [licenseOpen, setLicenseOpen] = useState(false);
  const [insuranceOpen, setInsuranceOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);

  const [registerForm, setRegisterForm] = useState(emptyRegisterForm);
  const [currentForm, setCurrentForm] = useState(emptyCurrentForm);
  const [licenseForm, setLicenseForm] = useState(emptyLicenseForm);
  const [insuranceForm, setInsuranceForm] = useState(emptyInsuranceForm);
  const [alertForm, setAlertForm] = useState(emptyAlertForm);

  const [selectedRegisterVehicle, setSelectedRegisterVehicle] = useState(null);
  const [selectedLicenseVehicle, setSelectedLicenseVehicle] = useState(null);
  const [selectedInsuranceVehicle, setSelectedInsuranceVehicle] = useState(null);

  const fetchVehicles = useCallback(async () => {
    try {
      const res = await axios.get(`${apiBase}/vehicles`, { params: { status: 1 } });
      setVehicles(res.data || []);
    } catch (error) {
      setVehicles([]);
    }
  }, []);

  const fetchCurrentDetails = useCallback(async () => {
    setCurrentLoading(true);
    try {
      const res = await axios.get(`${apiBase}/vehicles/current-details`, { params: currentFilters });
      setCurrentDetails(res.data || []);
    } catch (error) {
      setCurrentDetails([]);
      toast.error('Failed to load current vehicle details');
    } finally {
      setCurrentLoading(false);
    }
  }, [currentFilters]);

  const fetchLicenseHistory = useCallback(async () => {
    setLicenseLoading(true);
    try {
      const res = await axios.get(`${apiBase}/license-renewals`, { params: licenseFilters });
      setLicenseHistory(res.data || []);
    } catch (error) {
      setLicenseHistory([]);
      toast.error('Failed to load license history');
    } finally {
      setLicenseLoading(false);
    }
  }, [licenseFilters]);

  const fetchInsuranceHistory = useCallback(async () => {
    setInsuranceLoading(true);
    try {
      const res = await axios.get(`${apiBase}/insurance-renewals`, { params: insuranceFilters });
      setInsuranceHistory(res.data || []);
    } catch (error) {
      setInsuranceHistory([]);
      toast.error('Failed to load insurance history');
    } finally {
      setInsuranceLoading(false);
    }
  }, [insuranceFilters]);

  const fetchUpcomingRenewals = useCallback(async () => {
    setUpcomingLoading(true);
    try {
      const res = await axios.get(`${apiBase}/upcoming-renewals`, { params: upcomingFilters });
      setUpcomingRenewals(res.data || []);
    } catch (error) {
      setUpcomingRenewals([]);
      toast.error('Failed to load upcoming renewals');
    } finally {
      setUpcomingLoading(false);
    }
  }, [upcomingFilters]);

  const fetchAlertEmails = useCallback(async () => {
    setAlertLoading(true);
    try {
      const params = {
        search: alertFilters.search,
        status: alertFilters.status,
      };
      const res = await axios.get(`${apiBase}/alert-emails`, { params });
      setAlertEmails(res.data || []);
    } catch (error) {
      setAlertEmails([]);
      toast.error('Failed to load alert emails');
    } finally {
      setAlertLoading(false);
    }
  }, [alertFilters]);

  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  useEffect(() => {
    fetchCurrentDetails();
  }, [fetchCurrentDetails]);

  useEffect(() => {
    fetchLicenseHistory();
  }, [fetchLicenseHistory]);

  useEffect(() => {
    fetchInsuranceHistory();
  }, [fetchInsuranceHistory]);

  useEffect(() => {
    fetchUpcomingRenewals();
  }, [fetchUpcomingRenewals]);

  useEffect(() => {
    fetchAlertEmails();
  }, [fetchAlertEmails]);

  const refreshAfterRegister = async () => {
    await Promise.all([
      fetchVehicles(),
      fetchCurrentDetails(),
      fetchLicenseHistory(),
      fetchInsuranceHistory(),
      fetchUpcomingRenewals(),
    ]);
  };

  const refreshAfterLicense = async () => {
    await Promise.all([fetchCurrentDetails(), fetchLicenseHistory(), fetchUpcomingRenewals()]);
  };

  const refreshAfterInsurance = async () => {
    await Promise.all([fetchCurrentDetails(), fetchInsuranceHistory(), fetchUpcomingRenewals()]);
  };

  const openRegisterDialog = () => {
    setSelectedRegisterVehicle(null);
    setRegisterForm(emptyRegisterForm);
    setRegisterOpen(true);
  };

  const openCurrentEditDialog = (row) => {
    setCurrentForm({
      vehicle_id: row.vehicle_id || '',
      vehicle_number: row.vehicle_number || '',
      model: row.vehicle_model || '',
      reference: row.reference || '',
      licensed_date: toDateInputValue(row.current_licensed_date),
      insured_date: toDateInputValue(row.current_insured_date),
      insurance_agent_details: row.insurance_agent_details || '',
      license_reference_or_note: row.license_reference_or_note || '',
      insurance_reference_or_note: row.insurance_reference_or_note || '',
    });
    setCurrentEditOpen(true);
  };

  const openLicenseDialog = (row) => {
    const vehicle = row
      ? vehicles.find((item) => Number(item.id) === Number(row.vehicle_id)) || {
        id: row.vehicle_id,
        vehicle_number: row.vehicle_number,
        model: row.vehicle_model,
      }
      : null;

    setSelectedLicenseVehicle(vehicle);
    setLicenseForm({
      ...emptyLicenseForm,
      vehicle_id: vehicle?.id || '',
    });
    setLicenseOpen(true);
  };

  const openInsuranceDialog = (row) => {
    const vehicle = row
      ? vehicles.find((item) => Number(item.id) === Number(row.vehicle_id)) || {
        id: row.vehicle_id,
        vehicle_number: row.vehicle_number,
        model: row.vehicle_model,
      }
      : null;

    setSelectedInsuranceVehicle(vehicle);
    setInsuranceForm({
      ...emptyInsuranceForm,
      vehicle_id: vehicle?.id || '',
    });
    setInsuranceOpen(true);
  };

  const openAlertDialog = (row) => {
    setAlertForm(
      row
        ? {
          id: row.id,
          email_address: row.email_address || '',
          name_or_description: row.name_or_description || '',
          status: Number(row.status) === 1 ? 1 : 0,
        }
        : emptyAlertForm
    );
    setAlertOpen(true);
  };

  const saveCurrentDetails = async () => {
    if (currentSaving) return;

    if (!currentForm.vehicle_number.trim()) {
      toast.error('Vehicle number is required');
      return;
    }
    if (!currentForm.model.trim()) {
      toast.error('Vehicle model is required');
      return;
    }
    if (!currentForm.licensed_date || !currentForm.insured_date) {
      toast.error('Licensed date and insured date are required');
      return;
    }

    setCurrentSaving(true);
    try {
      await axios.put(`${apiBase}/vehicles/${currentForm.vehicle_id}/current`, currentForm);
      toast.success('Current vehicle details updated');
      setCurrentEditOpen(false);
      await refreshAfterRegister();
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to update current vehicle details');
      if (error?.response?.status === 409) toast.warn(message);
      else toast.error(message);
    } finally {
      setCurrentSaving(false);
    }
  };

  const deleteCurrentDetails = async (row) => {
    const confirmed = window.confirm('Delete this current vehicle details record?');
    if (!confirmed) return;

    setCurrentDeletingId(row.vehicle_id);
    try {
      await axios.delete(`${apiBase}/vehicles/${row.vehicle_id}/current`);
      toast.success('Current vehicle details deleted');
      await refreshAfterRegister();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete current vehicle details'));
    } finally {
      setCurrentDeletingId(null);
    }
  };

  const saveRegister = async () => {
    if (registerSaving) return;

    if (!registerForm.vehicle_id && !registerForm.vehicle_number.trim()) {
      toast.error('Vehicle number or selected vehicle is required');
      return;
    }
    if (!registerForm.vehicle_id && !registerForm.model.trim()) {
      toast.error('Vehicle model is required');
      return;
    }
    if (!registerForm.licensed_date || !registerForm.insured_date) {
      toast.error('Last licensed date and last insured date are required');
      return;
    }

    setRegisterSaving(true);
    try {
      await axios.post(`${apiBase}/register`, registerForm);
      toast.success('Vehicle license and insurance details registered');
      setRegisterOpen(false);
      await refreshAfterRegister();
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to register vehicle details');
      if (error?.response?.status === 409) toast.warn(message);
      else toast.error(message);
    } finally {
      setRegisterSaving(false);
    }
  };

  const saveLicenseRenewal = async () => {
    if (!licenseForm.vehicle_id) {
      toast.error('Select a vehicle');
      return;
    }
    if (!licenseForm.licensed_date) {
      toast.error('Licensed date is required');
      return;
    }

    try {
      await axios.post(`${apiBase}/license-renewals`, licenseForm);
      toast.success('License renewal added');
      setLicenseOpen(false);
      await refreshAfterLicense();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to add license renewal'));
    }
  };

  const saveInsuranceRenewal = async () => {
    if (insuranceSaving) return;

    if (!insuranceForm.vehicle_id) {
      toast.error('Select a vehicle');
      return;
    }
    if (!insuranceForm.insured_date) {
      toast.error('Insured date is required');
      return;
    }

    setInsuranceSaving(true);
    try {
      await axios.post(`${apiBase}/insurance-renewals`, insuranceForm);
      toast.success('Insurance renewal added');
      setInsuranceOpen(false);
      await refreshAfterInsurance();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to add insurance renewal'));
    } finally {
      setInsuranceSaving(false);
    }
  };

  const saveAlertEmail = async () => {
    const email = alertForm.email_address.trim();
    if (!email) {
      toast.error('Alert email address is required');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Alert email address is invalid');
      return;
    }

    try {
      if (alertForm.id) {
        await axios.put(`${apiBase}/alert-emails/${alertForm.id}`, alertForm);
        toast.success('Alert email updated');
      } else {
        await axios.post(`${apiBase}/alert-emails`, alertForm);
        toast.success('Alert email created');
      }
      setAlertOpen(false);
      await fetchAlertEmails();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save alert email'));
    }
  };

  const updateAlertStatus = async (row) => {
    const nextStatus = Number(row.status) === 1 ? 0 : 1;
    try {
      await axios.patch(`${apiBase}/alert-emails/${row.id}/status`, { status: nextStatus });
      toast.success('Alert email status updated');
      await fetchAlertEmails();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update alert email status'));
    }
  };

  const deleteAlertEmail = async (row) => {
    const confirmed = window.confirm('Deactivate this alert email?');
    if (!confirmed) return;

    try {
      await axios.delete(`${apiBase}/alert-emails/${row.id}`);
      toast.success('Alert email deactivated');
      await fetchAlertEmails();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to deactivate alert email'));
    }
  };

  const setVehicleHistoryFilter = (row, tabIndex) => {
    if (tabIndex === 1) {
      setLicenseFilters((prev) => ({ ...prev, vehicle_number: row.vehicle_number || '' }));
    } else {
      setInsuranceFilters((prev) => ({ ...prev, vehicle_number: row.vehicle_number || '' }));
    }
    setActiveTab(tabIndex);
  };

  return (
    <Container maxWidth={false} sx={{ paddingTop: '15vh', pb: 4 }}>
      <Card sx={{ padding: '15px' }}>
        <CardHeader title="Vehicle License Details" />
        <Divider />

        <Tabs value={activeTab} onChange={(_event, value) => setActiveTab(value)} variant="scrollable">
          <Tab label="Vehicle Register / Current Details" />
          <Tab label="License Renewal History" />
          <Tab label="Insurance Renewal History" />
          <Tab label="Upcoming Renewals" />
          <Tab label="Alert Email Settings" />
        </Tabs>
        <Divider />

        {activeTab === 0 && (
          <Box mt={2}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  label="Vehicle Number"
                  value={currentFilters.vehicle_number}
                  onChange={(e) => setCurrentFilters((prev) => ({ ...prev, vehicle_number: e.target.value }))}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  label="From Due Date"
                  type="date"
                  value={currentFilters.from_date}
                  onChange={(e) => setCurrentFilters((prev) => ({ ...prev, from_date: e.target.value }))}
                  size="small"
                  fullWidth
                  {...dateInputProps}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  label="To Due Date"
                  type="date"
                  value={currentFilters.to_date}
                  onChange={(e) => setCurrentFilters((prev) => ({ ...prev, to_date: e.target.value }))}
                  size="small"
                  fullWidth
                  {...dateInputProps}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button variant="outlined" startIcon={<SearchIcon />} onClick={fetchCurrentDetails} fullWidth>
                  Search
                </Button>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box display="flex" justifyContent="flex-end">
                  <Button variant="contained" endIcon={<AddCircleOutlineIcon />} onClick={openRegisterDialog}>
                    Register / Link
                  </Button>
                </Box>
              </Grid>
            </Grid>

            <TableContainer sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Vehicle No</TableCell>
                    <TableCell>Model</TableCell>
                    <TableCell>Reference</TableCell>
                    <TableCell>Licensed Date</TableCell>
                    <TableCell>Next License Date</TableCell>
                    <TableCell>Insured Date</TableCell>
                    <TableCell>Next Insurance Date</TableCell>
                    <TableCell>Insurance Agent</TableCell>
                    <TableCell>License Status</TableCell>
                    <TableCell>Insurance Status</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentDetails.length === 0 ? (
                    <EmptyRow colSpan={11} loading={currentLoading} />
                  ) : (
                    currentDetails.map((row) => (
                      <TableRow key={row.vehicle_id}>
                        <TableCell>{row.vehicle_number || '-'}</TableCell>
                        <TableCell>{row.vehicle_model || '-'}</TableCell>
                        <TableCell>{row.reference || '-'}</TableCell>
                        <TableCell>{formatDate(row.current_licensed_date)}</TableCell>
                        <TableCell>{formatDate(row.next_license_renewal_date)}</TableCell>
                        <TableCell>{formatDate(row.current_insured_date)}</TableCell>
                        <TableCell>{formatDate(row.next_insurance_renewal_date)}</TableCell>
                        <TableCell>{row.insurance_agent_details || '-'}</TableCell>
                        <TableCell>
                          <RenewalStatusChip status={row.license_status} />
                        </TableCell>
                        <TableCell>
                          <RenewalStatusChip status={row.insurance_status} />
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={1} justifyContent="center">
                            <IconButton
                              size="small"
                              aria-label="Edit current details"
                              onClick={() => openCurrentEditDialog(row)}
                              disabled={Number(currentDeletingId) === Number(row.vehicle_id)}
                            >
                              <CreateIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              aria-label="Delete current details"
                              onClick={() => deleteCurrentDetails(row)}
                              disabled={Number(currentDeletingId) === Number(row.vehicle_id)}
                            >
                              {Number(currentDeletingId) === Number(row.vehicle_id) ? (
                                <BeatLoader color="#d32f2f" size={4} />
                              ) : (
                                <DeleteIcon fontSize="small" />
                              )}
                            </IconButton>
                            <Button size="small" onClick={() => openLicenseDialog(row)}>
                              Add License
                            </Button>
                            <Button size="small" onClick={() => openInsuranceDialog(row)}>
                              Add Insurance
                            </Button>
                            <IconButton size="small" onClick={() => setVehicleHistoryFilter(row, 1)}>
                              <HistoryIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {activeTab === 1 && (
          <Box mt={2}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  label="Vehicle Number"
                  value={licenseFilters.vehicle_number}
                  onChange={(e) => setLicenseFilters((prev) => ({ ...prev, vehicle_number: e.target.value }))}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  label="Date Type"
                  value={licenseFilters.date_type}
                  onChange={(e) => setLicenseFilters((prev) => ({ ...prev, date_type: e.target.value }))}
                  size="small"
                  fullWidth
                >
                  <MenuItem value="licensed_date">Licensed Date</MenuItem>
                  <MenuItem value="next_license_renewal_date">Next License Renewal Date</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  label="From Date"
                  type="date"
                  value={licenseFilters.from_date}
                  onChange={(e) => setLicenseFilters((prev) => ({ ...prev, from_date: e.target.value }))}
                  size="small"
                  fullWidth
                  {...dateInputProps}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  label="To Date"
                  type="date"
                  value={licenseFilters.to_date}
                  onChange={(e) => setLicenseFilters((prev) => ({ ...prev, to_date: e.target.value }))}
                  size="small"
                  fullWidth
                  {...dateInputProps}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button variant="contained" endIcon={<AddCircleOutlineIcon />} onClick={() => openLicenseDialog()}>
                  Add License
                </Button>
              </Grid>
            </Grid>

            <TableContainer sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Vehicle No</TableCell>
                    <TableCell>Model</TableCell>
                    <TableCell>Licensed Date</TableCell>
                    <TableCell>Next License Date</TableCell>
                    <TableCell>Reference / Note</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created Date</TableCell>
                    <TableCell>Updated Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {licenseHistory.length === 0 ? (
                    <EmptyRow colSpan={8} loading={licenseLoading} />
                  ) : (
                    licenseHistory.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>{row.vehicle_number || '-'}</TableCell>
                        <TableCell>{row.vehicle_model || '-'}</TableCell>
                        <TableCell>{formatDate(row.licensed_date)}</TableCell>
                        <TableCell>{formatDate(row.next_license_renewal_date)}</TableCell>
                        <TableCell>{row.reference_or_note || '-'}</TableCell>
                        <TableCell>
                          <RenewalStatusChip status={row.status} />
                        </TableCell>
                        <TableCell>{formatDate(row.created_at)}</TableCell>
                        <TableCell>{formatDate(row.updated_at)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {activeTab === 2 && (
          <Box mt={2}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  label="Vehicle Number"
                  value={insuranceFilters.vehicle_number}
                  onChange={(e) => setInsuranceFilters((prev) => ({ ...prev, vehicle_number: e.target.value }))}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  label="Date Type"
                  value={insuranceFilters.date_type}
                  onChange={(e) => setInsuranceFilters((prev) => ({ ...prev, date_type: e.target.value }))}
                  size="small"
                  fullWidth
                >
                  <MenuItem value="insured_date">Insured Date</MenuItem>
                  <MenuItem value="next_insurance_renewal_date">Next Insurance Renewal Date</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  label="From Date"
                  type="date"
                  value={insuranceFilters.from_date}
                  onChange={(e) => setInsuranceFilters((prev) => ({ ...prev, from_date: e.target.value }))}
                  size="small"
                  fullWidth
                  {...dateInputProps}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  label="To Date"
                  type="date"
                  value={insuranceFilters.to_date}
                  onChange={(e) => setInsuranceFilters((prev) => ({ ...prev, to_date: e.target.value }))}
                  size="small"
                  fullWidth
                  {...dateInputProps}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button variant="contained" endIcon={<AddCircleOutlineIcon />} onClick={() => openInsuranceDialog()}>
                  Add Insurance
                </Button>
              </Grid>
            </Grid>

            <TableContainer sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Vehicle No</TableCell>
                    <TableCell>Model</TableCell>
                    <TableCell>Insured Date</TableCell>
                    <TableCell>Next Insurance Date</TableCell>
                    <TableCell>Insurance Agent</TableCell>
                    <TableCell>Reference / Note</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created Date</TableCell>
                    <TableCell>Updated Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {insuranceHistory.length === 0 ? (
                    <EmptyRow colSpan={9} loading={insuranceLoading} />
                  ) : (
                    insuranceHistory.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>{row.vehicle_number || '-'}</TableCell>
                        <TableCell>{row.vehicle_model || '-'}</TableCell>
                        <TableCell>{formatDate(row.insured_date)}</TableCell>
                        <TableCell>{formatDate(row.next_insurance_renewal_date)}</TableCell>
                        <TableCell>{row.insurance_agent_details || '-'}</TableCell>
                        <TableCell>{row.reference_or_note || '-'}</TableCell>
                        <TableCell>
                          <RenewalStatusChip status={row.status} />
                        </TableCell>
                        <TableCell>{formatDate(row.created_at)}</TableCell>
                        <TableCell>{formatDate(row.updated_at)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {activeTab === 3 && (
          <Box mt={2}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  label="Vehicle Number"
                  value={upcomingFilters.vehicle_number}
                  onChange={(e) => setUpcomingFilters((prev) => ({ ...prev, vehicle_number: e.target.value }))}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  select
                  label="Renewal Type"
                  value={upcomingFilters.renewal_type}
                  onChange={(e) => setUpcomingFilters((prev) => ({ ...prev, renewal_type: e.target.value }))}
                  size="small"
                  fullWidth
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="License">License</MenuItem>
                  <MenuItem value="Insurance">Insurance</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  label="From Due Date"
                  type="date"
                  value={upcomingFilters.from_date}
                  onChange={(e) => setUpcomingFilters((prev) => ({ ...prev, from_date: e.target.value }))}
                  size="small"
                  fullWidth
                  {...dateInputProps}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  label="To Due Date"
                  type="date"
                  value={upcomingFilters.to_date}
                  onChange={(e) => setUpcomingFilters((prev) => ({ ...prev, to_date: e.target.value }))}
                  size="small"
                  fullWidth
                  {...dateInputProps}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button variant="outlined" startIcon={<SearchIcon />} onClick={fetchUpcomingRenewals} fullWidth>
                  Search
                </Button>
              </Grid>
            </Grid>

            <TableContainer sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Vehicle No</TableCell>
                    <TableCell>Model</TableCell>
                    <TableCell>Renewal Type</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell>Days Remaining</TableCell>
                    <TableCell>Current Status</TableCell>
                    <TableCell>Reference / Note</TableCell>
                    <TableCell>Insurance Agent</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {upcomingRenewals.length === 0 ? (
                    <EmptyRow colSpan={8} loading={upcomingLoading} />
                  ) : (
                    upcomingRenewals.map((row, index) => (
                      <TableRow key={`${row.renewal_type}-${row.vehicle_id}-${index}`}>
                        <TableCell>{row.vehicle_number || '-'}</TableCell>
                        <TableCell>{row.vehicle_model || '-'}</TableCell>
                        <TableCell>{row.renewal_type}</TableCell>
                        <TableCell>{formatDate(row.renewal_due_date)}</TableCell>
                        <TableCell>{row.days_remaining}</TableCell>
                        <TableCell>{row.current_status || 'Current'}</TableCell>
                        <TableCell>{row.reference_or_note || '-'}</TableCell>
                        <TableCell>{row.insurance_agent_details || '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {activeTab === 4 && (
          <Box mt={2}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  label="Email Search"
                  value={alertFilters.search}
                  onChange={(e) => setAlertFilters((prev) => ({ ...prev, search: e.target.value }))}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  label="Status"
                  value={alertFilters.status}
                  onChange={(e) => setAlertFilters((prev) => ({ ...prev, status: e.target.value }))}
                  size="small"
                  fullWidth
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value={1}>Active</MenuItem>
                  <MenuItem value={0}>Inactive</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={5}>
                <Box display="flex" justifyContent="flex-end">
                  <Button variant="contained" endIcon={<AddCircleOutlineIcon />} onClick={() => openAlertDialog()}>
                    Add Email
                  </Button>
                </Box>
              </Grid>
            </Grid>

            <TableContainer sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Email Address</TableCell>
                    <TableCell>Name / Description</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created Date</TableCell>
                    <TableCell>Updated Date</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {alertEmails.length === 0 ? (
                    <EmptyRow colSpan={6} loading={alertLoading} />
                  ) : (
                    alertEmails.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>{row.email_address}</TableCell>
                        <TableCell>{row.name_or_description || '-'}</TableCell>
                        <TableCell>
                          <ActiveStatusChip status={row.status} />
                        </TableCell>
                        <TableCell>{formatDate(row.created_at)}</TableCell>
                        <TableCell>{formatDate(row.updated_at)}</TableCell>
                        <TableCell align="center">
                          <IconButton size="small" onClick={() => openAlertDialog(row)}>
                            <CreateIcon fontSize="small" />
                          </IconButton>
                          <Button size="small" onClick={() => updateAlertStatus(row)}>
                            {Number(row.status) === 1 ? 'Deactivate' : 'Activate'}
                          </Button>
                          <IconButton size="small" onClick={() => deleteAlertEmail(row)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Card>

      <Dialog open={registerOpen} onClose={() => !registerSaving && setRegisterOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Register Vehicle License and Insurance</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                options={vehicles}
                value={selectedRegisterVehicle}
                inputValue={registerForm.vehicle_number}
                getOptionLabel={getVehicleLabel}
                isOptionEqualToValue={(option, value) => Number(option?.id) === Number(value?.id)}
                onInputChange={(_event, value, reason) => {
                  setRegisterForm((prev) => ({
                    ...prev,
                    vehicle_number: value,
                    vehicle_id: reason === 'input' ? '' : prev.vehicle_id,
                  }));
                  if (reason === 'input') setSelectedRegisterVehicle(null);
                }}
                onChange={(_event, value) => {
                  if (value && typeof value !== 'string') {
                    setSelectedRegisterVehicle(value);
                    setRegisterForm((prev) => ({
                      ...prev,
                      vehicle_id: value.id,
                      vehicle_number: value.vehicle_number || '',
                      model: value.model || '',
                      reference: value.reference || '',
                    }));
                  }
                }}
                renderInput={(params) => <TextField {...params} label="Vehicle Number" fullWidth required />}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Model"
                value={registerForm.model}
                onChange={(e) => setRegisterForm((prev) => ({ ...prev, model: e.target.value }))}
                disabled={Boolean(selectedRegisterVehicle)}
                fullWidth
                required={!selectedRegisterVehicle}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Reference"
                value={registerForm.reference}
                onChange={(e) => setRegisterForm((prev) => ({ ...prev, reference: e.target.value }))}
                disabled={Boolean(selectedRegisterVehicle)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Last Licensed Date"
                type="date"
                value={registerForm.licensed_date}
                onChange={(e) => setRegisterForm((prev) => ({ ...prev, licensed_date: e.target.value }))}
                fullWidth
                required
                {...dateInputProps}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Last Insured Date"
                type="date"
                value={registerForm.insured_date}
                onChange={(e) => setRegisterForm((prev) => ({ ...prev, insured_date: e.target.value }))}
                fullWidth
                required
                {...dateInputProps}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Insurance Agent Details"
                value={registerForm.insurance_agent_details}
                onChange={(e) => setRegisterForm((prev) => ({ ...prev, insurance_agent_details: e.target.value }))}
                fullWidth
                multiline
                minRows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegisterOpen(false)} disabled={registerSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveRegister} disabled={registerSaving} sx={{ minWidth: 90 }}>
            {registerSaving ? <BeatLoader color="#ffffff" size={8} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={currentEditOpen} onClose={() => !currentSaving && setCurrentEditOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Update Current Vehicle Details</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Vehicle Number"
                value={currentForm.vehicle_number}
                onChange={(e) => setCurrentForm((prev) => ({ ...prev, vehicle_number: e.target.value }))}
                disabled={currentSaving}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Model"
                value={currentForm.model}
                onChange={(e) => setCurrentForm((prev) => ({ ...prev, model: e.target.value }))}
                disabled={currentSaving}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Reference"
                value={currentForm.reference}
                onChange={(e) => setCurrentForm((prev) => ({ ...prev, reference: e.target.value }))}
                disabled={currentSaving}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Licensed Date"
                type="date"
                value={currentForm.licensed_date}
                onChange={(e) => setCurrentForm((prev) => ({ ...prev, licensed_date: e.target.value }))}
                disabled={currentSaving}
                fullWidth
                required
                {...dateInputProps}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Insured Date"
                type="date"
                value={currentForm.insured_date}
                onChange={(e) => setCurrentForm((prev) => ({ ...prev, insured_date: e.target.value }))}
                disabled={currentSaving}
                fullWidth
                required
                {...dateInputProps}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Insurance Agent Details"
                value={currentForm.insurance_agent_details}
                onChange={(e) => setCurrentForm((prev) => ({ ...prev, insurance_agent_details: e.target.value }))}
                disabled={currentSaving}
                fullWidth
                multiline
                minRows={3}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="License Reference / Note"
                value={currentForm.license_reference_or_note}
                onChange={(e) => setCurrentForm((prev) => ({ ...prev, license_reference_or_note: e.target.value }))}
                disabled={currentSaving}
                fullWidth
                multiline
                minRows={3}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Insurance Reference / Note"
                value={currentForm.insurance_reference_or_note}
                onChange={(e) =>
                  setCurrentForm((prev) => ({ ...prev, insurance_reference_or_note: e.target.value }))
                }
                disabled={currentSaving}
                fullWidth
                multiline
                minRows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCurrentEditOpen(false)} disabled={currentSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveCurrentDetails} disabled={currentSaving} sx={{ minWidth: 90 }}>
            {currentSaving ? <BeatLoader color="#ffffff" size={8} /> : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={licenseOpen} onClose={() => setLicenseOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add License Renewal</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                options={vehicles}
                value={selectedLicenseVehicle}
                getOptionLabel={getVehicleLabel}
                isOptionEqualToValue={(option, value) => Number(option?.id) === Number(value?.id)}
                onChange={(_event, value) => {
                  setSelectedLicenseVehicle(value);
                  setLicenseForm((prev) => ({ ...prev, vehicle_id: value?.id || '' }));
                }}
                renderInput={(params) => <TextField {...params} label="Vehicle" required />}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Licensed Date"
                type="date"
                value={licenseForm.licensed_date}
                onChange={(e) => setLicenseForm((prev) => ({ ...prev, licensed_date: e.target.value }))}
                fullWidth
                required
                {...dateInputProps}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Reference / Note"
                value={licenseForm.reference_or_note}
                onChange={(e) => setLicenseForm((prev) => ({ ...prev, reference_or_note: e.target.value }))}
                fullWidth
                multiline
                minRows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLicenseOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveLicenseRenewal}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={insuranceOpen} onClose={() => !insuranceSaving && setInsuranceOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add Insurance Renewal</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                options={vehicles}
                value={selectedInsuranceVehicle}
                getOptionLabel={getVehicleLabel}
                isOptionEqualToValue={(option, value) => Number(option?.id) === Number(value?.id)}
                onChange={(_event, value) => {
                  setSelectedInsuranceVehicle(value);
                  setInsuranceForm((prev) => ({ ...prev, vehicle_id: value?.id || '' }));
                }}
                renderInput={(params) => <TextField {...params} label="Vehicle" required />}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Insured Date"
                type="date"
                value={insuranceForm.insured_date}
                onChange={(e) => setInsuranceForm((prev) => ({ ...prev, insured_date: e.target.value }))}
                fullWidth
                required
                {...dateInputProps}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Insurance Agent Details"
                value={insuranceForm.insurance_agent_details}
                onChange={(e) => setInsuranceForm((prev) => ({ ...prev, insurance_agent_details: e.target.value }))}
                fullWidth
                multiline
                minRows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Reference / Note"
                value={insuranceForm.reference_or_note}
                onChange={(e) => setInsuranceForm((prev) => ({ ...prev, reference_or_note: e.target.value }))}
                fullWidth
                multiline
                minRows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInsuranceOpen(false)} disabled={insuranceSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveInsuranceRenewal} disabled={insuranceSaving} sx={{ minWidth: 90 }}>
            {insuranceSaving ? <BeatLoader color="#ffffff" size={8} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={alertOpen} onClose={() => setAlertOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{alertForm.id ? 'Edit Alert Email' : 'Add Alert Email'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Email Address"
                value={alertForm.email_address}
                onChange={(e) => setAlertForm((prev) => ({ ...prev, email_address: e.target.value }))}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Name / Description"
                value={alertForm.name_or_description}
                onChange={(e) => setAlertForm((prev) => ({ ...prev, name_or_description: e.target.value }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                label="Status"
                value={alertForm.status}
                onChange={(e) => setAlertForm((prev) => ({ ...prev, status: Number(e.target.value) }))}
                fullWidth
              >
                <MenuItem value={1}>Active</MenuItem>
                <MenuItem value={0}>Inactive</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAlertOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveAlertEmail}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default VehicleLicenseDetails;
